import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import ClausuraVista from './ClausuraVista';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SuperLigaVista = ({ isAdmin, adminPassword }) => {
  const [vista, setVista] = useState('fixture');
  const [grupoSeleccionado, setGrupoSeleccionado] = useState('A');
  const [fixtureGrupo, setFixtureGrupo] = useState([]);
  const [tablaGrupo, setTablaGrupo] = useState([]);
  const [resultados, setResultados] = useState({});
  const [cuartos, setCuartos] = useState([]);
  const [semis, setSemis] = useState([]);
  const [final, setFinal] = useState({});
  const [loading, setLoading] = useState(false);
  
  // Estados para goleadores
  const [goleadores, setGoleadores] = useState([]);
  const [clubesSuperliga, setClubesSuperliga] = useState([]);
  const [mostrarFormGoleador, setMostrarFormGoleador] = useState(false);
  const [nuevoGoleador, setNuevoGoleador] = useState({ nombre: '', goles: 0, club: '' });
  const [editandoGoleador, setEditandoGoleador] = useState(null);
  const [busquedaGoleador, setBusquedaGoleador] = useState('');

  // Filtrar goleadores por búsqueda
  const goleadoresFiltrados = busquedaGoleador.trim() 
    ? goleadores.filter(g => g.nombre.toLowerCase().includes(busquedaGoleador.toLowerCase()))
    : goleadores;

  const cargarDatos = useCallback(async (grupo) => {
    setLoading(true);
    try {
      const [fixtureRes, tablaRes, resultadosRes] = await Promise.all([
        axios.get(`${API}/superliga/fixture/${grupo}`),
        axios.get(`${API}/superliga/tabla/${grupo}`),
        axios.get(`${API}/superliga/resultados/${grupo}`)
      ]);
      setFixtureGrupo(fixtureRes.data.fechas || []);
      setTablaGrupo(tablaRes.data || []);
      setResultados(resultadosRes.data || {});
    } catch (error) {
      console.error('Error:', error);
    }
    setLoading(false);
  }, []);

  const cargarGoleadores = useCallback(async () => {
    try {
      const [goleadoresRes, clubesRes] = await Promise.all([
        axios.get(`${API}/superliga/goleadores`),
        axios.get(`${API}/superliga/clubes`)
      ]);
      setGoleadores(goleadoresRes.data || []);
      setClubesSuperliga(clubesRes.data || []);
    } catch (error) {
      console.error('Error cargando goleadores:', error);
    }
  }, []);

  const cargarFasesFinales = useCallback(async () => {
    try {
      const [cuartosRes, semisRes, finalRes] = await Promise.all([
        axios.get(`${API}/superliga/cuartos`),
        axios.get(`${API}/superliga/semis`),
        axios.get(`${API}/superliga/final`)
      ]);
      setCuartos(cuartosRes.data || []);
      setSemis(semisRes.data || []);
      setFinal(finalRes.data || {});
    } catch (error) {
      console.error('Error:', error);
    }
  }, []);

  useEffect(() => {
    cargarDatos(grupoSeleccionado);
  }, [grupoSeleccionado, cargarDatos]);

  useEffect(() => {
    if (vista === 'cuartos' || vista === 'semis' || vista === 'final') {
      cargarFasesFinales();
    }
    if (vista === 'goleadores') {
      cargarGoleadores();
    }
  }, [vista, cargarFasesFinales, cargarGoleadores]);

  const cambiarGrupo = (nuevoGrupo) => {
    if (nuevoGrupo !== grupoSeleccionado) {
      setFixtureGrupo([]);
      setTablaGrupo([]);
      setResultados({});
      setGrupoSeleccionado(nuevoGrupo);
    }
  };

  // Obtener resultado de un partido específico
  const getResultado = (fecha, partidoIdx) => {
    const key = `${fecha}_${partidoIdx}`;
    return resultados[key] || null;
  };

  const cargarResultado = async (fecha, partidoIdx, equipo1, equipo2) => {
    if (!isAdmin) return alert('Solo admin');
    
    const resultadoExistente = getResultado(fecha, partidoIdx);
    const mensajeG1 = resultadoExistente 
      ? `Goles ${equipo1} (actual: ${resultadoExistente.goles1}, dejar vacío para eliminar):` 
      : `Goles ${equipo1}:`;
    const mensajeG2 = resultadoExistente 
      ? `Goles ${equipo2} (actual: ${resultadoExistente.goles2}, dejar vacío para eliminar):` 
      : `Goles ${equipo2}:`;
    
    const g1 = prompt(mensajeG1);
    if (g1 === null) return; // Canceló
    
    const g2 = prompt(mensajeG2);
    if (g2 === null) return; // Canceló
    
    // Si ambos están vacíos, eliminar resultado
    const goles1 = g1.trim() === '' ? -1 : parseInt(g1);
    const goles2 = g2.trim() === '' ? -1 : parseInt(g2);
    
    // Validar que si uno tiene valor, el otro también (excepto para eliminar)
    if ((goles1 === -1 && goles2 !== -1) || (goles1 !== -1 && goles2 === -1)) {
      alert('Debes ingresar goles para ambos equipos o dejar ambos vacíos para eliminar');
      return;
    }
    
    try {
      const response = await axios.post(`${API}/superliga/resultado`, {
        grupo: grupoSeleccionado,
        fecha: fecha,
        partido_idx: partidoIdx,
        equipo1: equipo1,
        equipo2: equipo2,
        goles1: goles1,
        goles2: goles2
      });
      
      if (response.data.eliminado) {
        alert('✅ Resultado eliminado');
      } else {
        alert('✅ Resultado cargado');
      }
      cargarDatos(grupoSeleccionado);
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    }
  };

  const cargarCuarto = async (partidoIdx) => {
    if (!isAdmin) return alert('Solo admin');
    const eq1 = prompt('Equipo 1:');
    const eq2 = prompt('Equipo 2:');
    const ida1 = prompt('Goles Ida Equipo 1:');
    const ida2 = prompt('Goles Ida Equipo 2:');
    const vuelta1 = prompt('Goles Vuelta Equipo 1:');
    const vuelta2 = prompt('Goles Vuelta Equipo 2:');
    if (!eq1 || !eq2 || ida1 === null || ida2 === null || vuelta1 === null || vuelta2 === null) return;
    
    try {
      await axios.post(`${API}/superliga/cuartos`, {
        partido_idx: partidoIdx,
        equipo1: eq1,
        equipo2: eq2,
        goles_ida_1: parseInt(ida1),
        goles_ida_2: parseInt(ida2),
        goles_vuelta_1: parseInt(vuelta1),
        goles_vuelta_2: parseInt(vuelta2)
      });
      alert('✅ Cuarto cargado');
      cargarFasesFinales();
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    }
  };

  const cargarSemi = async (partidoIdx) => {
    if (!isAdmin) return alert('Solo admin');
    const eq1 = prompt('Equipo 1:');
    const eq2 = prompt('Equipo 2:');
    const ida1 = prompt('Goles Ida Equipo 1:');
    const ida2 = prompt('Goles Ida Equipo 2:');
    const vuelta1 = prompt('Goles Vuelta Equipo 1:');
    const vuelta2 = prompt('Goles Vuelta Equipo 2:');
    if (!eq1 || !eq2 || ida1 === null || ida2 === null || vuelta1 === null || vuelta2 === null) return;
    
    try {
      await axios.post(`${API}/superliga/semis`, {
        partido_idx: partidoIdx,
        equipo1: eq1,
        equipo2: eq2,
        goles_ida_1: parseInt(ida1),
        goles_ida_2: parseInt(ida2),
        goles_vuelta_1: parseInt(vuelta1),
        goles_vuelta_2: parseInt(vuelta2)
      });
      alert('✅ Semifinal cargada');
      cargarFasesFinales();
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Funciones para Goleadores
  const agregarGoleador = async () => {
    if (!nuevoGoleador.nombre || !nuevoGoleador.club) {
      alert('Debes completar nombre y club');
      return;
    }
    try {
      await axios.post(`${API}/superliga/goleadores`, nuevoGoleador);
      alert('✅ Goleador agregado');
      setNuevoGoleador({ nombre: '', goles: 0, club: '' });
      setMostrarFormGoleador(false);
      cargarGoleadores();
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    }
  };

  const actualizarGoleador = async (goleador) => {
    try {
      await axios.put(`${API}/superliga/goleadores/${goleador.id}`, {
        id: goleador.id,
        nombre: goleador.nombre,
        goles: goleador.goles,
        club: goleador.club
      });
      alert('✅ Goleador actualizado');
      setEditandoGoleador(null);
      cargarGoleadores();
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    }
  };

  const eliminarGoleador = async (id) => {
    if (!window.confirm('¿Eliminar este goleador?')) return;
    try {
      await axios.delete(`${API}/superliga/goleadores/${id}`);
      alert('✅ Goleador eliminado');
      cargarGoleadores();
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    }
  };

  const [tab, setTab] = useState('clausura'); // 'clausura' | 'archivados'

  const abrirPdfApertura = () => {
    window.open(`${API}/superliga/archivos/apertura-2026/view`, '_blank', 'noopener,noreferrer');
  };

  const descargarPdfApertura = () => {
    // Trigger native download via a temporary <a download> click (single click, no intermediate page)
    const url = `${API}/superliga/archivos/apertura-2026/download`;
    const a = document.createElement('a');
    a.href = url;
    a.download = 'Campeonato_Apertura_2026.pdf';
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <div style={{padding: '20px', background: '#f5f5f5', minHeight: '100vh'}} data-testid="superliga-vista">
      <h2 style={{textAlign: 'center', color: '#0055A4', marginBottom: '8px', fontSize: '2em'}}>🏆 SERIE A SUPERLIGA</h2>
      <p style={{textAlign: 'center', color: '#666', marginBottom: '30px'}}>CAMPEONATO DE FUTSAL FIFA DE PRIMERA DIVISIÓN</p>

      {/* Tabs de nivel superior: Clausura activo / Archivados */}
      <div style={{textAlign: 'center', marginBottom: '30px', display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap'}}>
        <button
          onClick={() => setTab('clausura')}
          data-testid="tab-clausura-btn"
          style={{padding: '12px 24px', background: tab === 'clausura' ? '#0055A4' : '#ccc', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', fontSize: '1em'}}
        >
          🏆 CLAUSURA 2026
        </button>
        <button
          onClick={() => setTab('archivados')}
          data-testid="tab-archivados-btn"
          style={{padding: '12px 24px', background: tab === 'archivados' ? '#0055A4' : '#ccc', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', fontSize: '1em'}}
        >
          📁 CAMPEONATOS ARCHIVADOS
        </button>
      </div>

      {/* CLAUSURA (activo por defecto, ahora con datos reales) */}
      {tab === 'clausura' && (
        <div data-testid="clausura-section">
          <ClausuraVista isAdmin={isAdmin} adminPassword={adminPassword} />
        </div>
      )}

      {/* ARCHIVADOS: tarjeta del Apertura 2026 con botones de PDF */}
      {tab === 'archivados' && (
        <div data-testid="archivados-section" style={{maxWidth: '820px', margin: '0 auto'}}>
          <h3 style={{color: '#0055A4', marginBottom: '16px', textAlign: 'center'}}>📁 CAMPEONATOS ARCHIVADOS</h3>

          <div style={{background: 'white', padding: '25px', borderRadius: '12px', boxShadow: '0 4px 14px rgba(0,0,0,0.1)', borderLeft: '6px solid #FFD700'}} data-testid="card-apertura-2026">
            <div style={{display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap'}}>
              <div style={{fontSize: '2.4em'}}>📄</div>
              <div style={{flex: 1, minWidth: '220px'}}>
                <div style={{fontSize: '1.15em', fontWeight: 'bold', color: '#0055A4'}}>CAMPEONATO APERTURA 2026</div>
                <div style={{fontSize: '0.9em', color: '#666', marginTop: '4px'}}>SuperLiga Serie A · Torneo archivado en PDF</div>
              </div>
            </div>

            <div style={{display: 'flex', gap: '10px', marginTop: '20px', flexWrap: 'wrap'}}>
              <button
                onClick={abrirPdfApertura}
                data-testid="btn-abrir-pdf-apertura"
                style={{flex: '1 1 200px', padding: '12px 20px', background: '#0055A4', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', fontSize: '1em', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px'}}
              >
                👁️ ABRIR PDF
              </button>
              <button
                onClick={descargarPdfApertura}
                data-testid="btn-descargar-pdf-apertura"
                style={{flex: '1 1 200px', padding: '12px 20px', background: '#28a745', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', fontSize: '1em', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px'}}
              >
                ⬇️ DESCARGAR PDF
              </button>
            </div>

            {isAdmin && (
              <div style={{marginTop: '18px', paddingTop: '14px', borderTop: '1px dashed #ddd'}}>
                <button
                  onClick={async () => {
                    if (!window.confirm('¿Regenerar el PDF con los datos actuales de la base de datos?')) return;
                    try {
                      const r = await axios.post(`${API}/superliga/archivos/apertura-2026/regenerar`);
                      alert(`✅ PDF regenerado (${(r.data.tamano_bytes / 1024).toFixed(1)} KB)`);
                    } catch (err) {
                      alert('❌ Error: ' + (err.response?.data?.detail || err.message));
                    }
                  }}
                  data-testid="btn-regenerar-pdf-apertura"
                  style={{padding: '8px 14px', background: '#ffc107', color: '#333', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.9em'}}
                >
                  🔄 Regenerar PDF (admin)
                </button>
                <span style={{marginLeft: '10px', fontSize: '0.85em', color: '#666'}}>Usar cuando actualices datos del Apertura.</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SuperLigaVista;
